import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { 
  NavigateNext as NavigateNextIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Search as SearchIcon,
  LocationOn as LocationOnIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';

// Cookie helpers for remembering selections on Start From Property tab
const setCookie = (name: string, value: string, days: number = 365) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
};

const getCookie = (name: string): string | null => {
  const segment = document.cookie.split('; ').find(r => r.startsWith(name + '='));
  if (!segment) return null;
  const [, val] = segment.split('=');
  return decodeURIComponent(val || '');
};

const deleteCookie = (name: string) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
};

const COOKIE_CONTINENT_ID = 'appraisals_new_selected_continent_id';
const COOKIE_COUNTRY_ID = 'appraisals_new_selected_country_id';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface HubSpotDeal {
  id: string;
  properties: {
    dealname: string;
    pipeline?: string;
    pipelineName?: string;
    dealstage?: string;
    dealStageName?: string;
    dealtype?: string;
    amount?: string;
    primary_property?: string;
    [key: string]: string | undefined;
  };
}

interface HubSpotDealsResponse {
  results: HubSpotDeal[];
  paging?: {
    next?: {
      after: string;
      link: string;
    };
  };
}

interface Continent {
  id: number;
  name: string;
  code: string;
  isActive: boolean;
  totalCountryCount: number;
  activeCountryCount: number;
  totalMarketCount: number;
  activeMarketCount: number;
}

interface Country {
  id: number;
  name: string;
  code: string;
  continentId: number;
  isActive: boolean;
  totalMarketCount: number;
  activeMarketCount: number;
}

interface Market {
  id: number;
  name: string;
  countryId: number;
  isActive: boolean;
  slug?: string;
}

interface Property {
  id: number;
  property_id: string;
  property_name: string;
  address?: string;
  city?: string;
  state_province?: string;
  postal_code?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  chain_name?: string;
  total_rooms?: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`appraisal-tabpanel-${index}`}
      aria-labelledby={`appraisal-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `appraisal-tab-${index}`,
    'aria-controls': `appraisal-tabpanel-${index}`,
  };
}

export const NewAppraisalPage: React.FC = () => {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [tabValue, setTabValue] = useState(0);
  const [bidDeals, setBidDeals] = useState<HubSpotDeal[]>([]);
  const [productionDeals, setProductionDeals] = useState<HubSpotDeal[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProduction, setLoadingProduction] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorProduction, setErrorProduction] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Property selection state
  const [continents, setContinents] = useState<Continent[]>([]);
  const [selectedContinent, setSelectedContinent] = useState<number | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<number | null>(null);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [selectedMarket, setSelectedMarket] = useState<number | null>(null);
  const [properties, setProperties] = useState<Property[]>([]);
  const [propertySearch, setPropertySearch] = useState('');
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [didInitFromCookies, setDidInitFromCookies] = useState(false);
  const [didInitCountryFromCookies, setDidInitCountryFromCookies] = useState(false);

  const handleBreadcrumbClick = (event: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    event.preventDefault();
    navigate(path);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Fetch HubSpot deals
  const fetchBidDeals = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Specify the properties we want to retrieve
      const properties = ['dealname', 'pipeline', 'dealstage', 'dealtype', 'amount', 'primary_property'].join(',');
      const response = await authenticatedFetch(
        `${API_URL}/api/hubspot/appraisal-bid-deals?properties=${properties}&limit=100&excludeExisting=true`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response) {
        throw new Error('No response from server');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data && result.data.results) {
        setBidDeals(result.data.results);
      } else {
        throw new Error(result.message || 'Failed to fetch HubSpot bid deals');
      }
    } catch (err) {
      console.error('Error fetching HubSpot bid deals:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch HubSpot bid deals');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  const fetchProductionDeals = useCallback(async () => {
    setLoadingProduction(true);
    setErrorProduction(null);
    
    try {
      // Specify the properties we want to retrieve
      const properties = ['dealname', 'pipeline', 'dealstage', 'dealtype', 'amount', 'primary_property'].join(',');
      const response = await authenticatedFetch(
        `${API_URL}/api/hubspot/production-appraisal-deals?properties=${properties}&limit=100&excludeExisting=true`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response) {
        throw new Error('No response from server');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data && result.data.results) {
        setProductionDeals(result.data.results);
      } else {
        throw new Error(result.message || 'Failed to fetch HubSpot production deals');
      }
    } catch (err) {
      console.error('Error fetching HubSpot production deals:', err);
      setErrorProduction(err instanceof Error ? err.message : 'Failed to fetch HubSpot production deals');
    } finally {
      setLoadingProduction(false);
    }
  }, [authenticatedFetch]);

  // Fetch continents for property selection
  const fetchContinents = useCallback(async () => {
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/markets/continents`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response) {
        throw new Error('No response from server');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        setContinents(result.data);
      }
    } catch (err) {
      console.error('Error fetching continents:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch continents');
    }
  }, [authenticatedFetch]);

  // Fetch countries when continent is selected
  const fetchCountries = useCallback(async (continentId: number) => {
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/markets/countries/${continentId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response) {
        throw new Error('No response from server');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        setCountries(result.data);
      }
    } catch (err) {
      console.error('Error fetching countries:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch countries');
    }
  }, [authenticatedFetch]);

  // Fetch markets when country is selected
  const fetchMarkets = useCallback(async (countryId: number) => {
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/markets/${countryId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response) {
        throw new Error('No response from server');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        setMarkets(result.data);
      }
    } catch (err) {
      console.error('Error fetching markets:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch markets');
    }
  }, [authenticatedFetch]);

  // Fetch properties when market is selected
  const fetchProperties = useCallback(async (marketId: number) => {
    setLoadingProperties(true);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/market-properties/markets/${marketId}/properties`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response) {
        throw new Error('No response from server');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        // The properties array is nested inside data.data
        const propertiesArray = result.data.data || result.data;
        if (Array.isArray(propertiesArray)) {
          setProperties(propertiesArray);
        } else {
          console.error('Properties response is not an array:', result);
          setProperties([]);
        }
      }
    } catch (err) {
      console.error('Error fetching properties:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch properties');
    } finally {
      setLoadingProperties(false);
    }
  }, [authenticatedFetch]);

  // Fetch bid deals when Bid Deal tab is selected
  useEffect(() => {
    if (tabValue === 0 && bidDeals.length === 0) {
      fetchBidDeals();
    }
  }, [tabValue, bidDeals.length, fetchBidDeals]);

  // Fetch production deals when Production Deal tab is selected
  useEffect(() => {
    if (tabValue === 1 && productionDeals.length === 0) {
      fetchProductionDeals();
    }
  }, [tabValue, productionDeals.length, fetchProductionDeals]);

  // Fetch continents when Property tab is selected
  useEffect(() => {
    if (tabValue === 2 && continents.length === 0) {
      fetchContinents();
    }
  }, [tabValue, continents.length, fetchContinents]);

  // Handle continent selection
  useEffect(() => {
    if (selectedContinent) {
      fetchCountries(selectedContinent);
      // Reset downstream selections
      setSelectedCountry(null);
      setSelectedMarket(null);
      setCountries([]);
      setMarkets([]);
      setProperties([]);
    }
  }, [selectedContinent, fetchCountries]);

  // Handle country selection
  useEffect(() => {
    if (selectedCountry) {
      fetchMarkets(selectedCountry);
      // Reset downstream selections
      setSelectedMarket(null);
      setMarkets([]);
      setProperties([]);
    }
  }, [selectedCountry, fetchMarkets]);

  // Handle market selection
  useEffect(() => {
    if (selectedMarket) {
      fetchProperties(selectedMarket);
    }
  }, [selectedMarket, fetchProperties]);

  // Initialize continent from cookie when entering Property tab and continents are loaded
  useEffect(() => {
    if (tabValue === 2 && continents.length > 0 && !didInitFromCookies) {
      const cookieContinent = getCookie(COOKIE_CONTINENT_ID);
      if (cookieContinent) {
        const continentId = parseInt(cookieContinent, 10);
        if (!isNaN(continentId) && continents.some(c => c.id === continentId)) {
          setSelectedContinent(continentId);
        }
      }
      setDidInitFromCookies(true);
    }
  }, [tabValue, continents, didInitFromCookies]);

  // Initialize country from cookie after countries load for selected continent
  useEffect(() => {
    if (tabValue === 2 && selectedContinent && countries.length > 0 && !didInitCountryFromCookies) {
      const cookieCountry = getCookie(COOKIE_COUNTRY_ID);
      if (cookieCountry) {
        const countryId = parseInt(cookieCountry, 10);
        if (!isNaN(countryId) && countries.some(c => c.id === countryId)) {
          setSelectedCountry(countryId);
        }
      }
      setDidInitCountryFromCookies(true);
    }
  }, [tabValue, selectedContinent, countries, didInitCountryFromCookies]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field: string) => {
    const isAsc = sortField === field && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortField(field);
  };

  // Sort bid deals based on current sort settings
  const sortedBidDeals = React.useMemo(() => {
    if (!sortField) return bidDeals;
    
    return [...bidDeals].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'dealname':
          aValue = a.properties.dealname || '';
          bValue = b.properties.dealname || '';
          break;
        case 'pipeline':
          aValue = a.properties.pipelineName || a.properties.pipeline || '';
          bValue = b.properties.pipelineName || b.properties.pipeline || '';
          break;
        case 'dealstage':
          aValue = a.properties.dealStageName || a.properties.dealstage || '';
          bValue = b.properties.dealStageName || b.properties.dealstage || '';
          break;
        case 'dealtype':
          aValue = a.properties.dealtype || '';
          bValue = b.properties.dealtype || '';
          break;
        case 'amount':
          aValue = parseFloat(a.properties.amount || '0');
          bValue = parseFloat(b.properties.amount || '0');
          break;
        case 'primary_property':
          aValue = a.properties.primary_property || '';
          bValue = b.properties.primary_property || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [bidDeals, sortField, sortDirection]);

  // Sort production deals based on current sort settings
  const sortedProductionDeals = React.useMemo(() => {
    if (!sortField) return productionDeals;
    
    return [...productionDeals].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'dealname':
          aValue = a.properties.dealname || '';
          bValue = b.properties.dealname || '';
          break;
        case 'pipeline':
          aValue = a.properties.pipelineName || a.properties.pipeline || '';
          bValue = b.properties.pipelineName || b.properties.pipeline || '';
          break;
        case 'dealstage':
          aValue = a.properties.dealStageName || a.properties.dealstage || '';
          bValue = b.properties.dealStageName || b.properties.dealstage || '';
          break;
        case 'dealtype':
          aValue = a.properties.dealtype || '';
          bValue = b.properties.dealtype || '';
          break;
        case 'amount':
          aValue = parseFloat(a.properties.amount || '0');
          bValue = parseFloat(b.properties.amount || '0');
          break;
        case 'primary_property':
          aValue = a.properties.primary_property || '';
          bValue = b.properties.primary_property || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [productionDeals, sortField, sortDirection]);

  // Filter properties based on search
  const filteredProperties = React.useMemo(() => {
    // Ensure properties is always an array
    const propertiesArray = Array.isArray(properties) ? properties : [];
    
    if (!propertySearch) return propertiesArray;
    
    const searchLower = propertySearch.toLowerCase();
    return propertiesArray.filter(property => {
      const propertyName = property.property_name?.toLowerCase() || '';
      const address = property.address?.toLowerCase() || '';
      const city = property.city?.toLowerCase() || '';
      const chainName = property.chain_name?.toLowerCase() || '';
      
      return propertyName.includes(searchLower) ||
             address.includes(searchLower) ||
             city.includes(searchLower) ||
             chainName.includes(searchLower);
    });
  }, [properties, propertySearch]);

  const formatAmount = (amount?: string) => {
    if (!amount) return '-';
    const num = parseFloat(amount);
    if (isNaN(num)) return amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const getDealStageColor = (stage?: string): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    if (!stage) return 'default';
    const lowerStage = stage.toLowerCase();
    if (lowerStage.includes('won') || lowerStage.includes('closed')) return 'success';
    if (lowerStage.includes('qualified')) return 'info';
    if (lowerStage.includes('proposal') || lowerStage.includes('presentation')) return 'warning';
    if (lowerStage.includes('lost')) return 'error';
    return 'default';
  };

  // Component for sortable table headers
  const SortableTableCell: React.FC<{ field: string; children: React.ReactNode; align?: 'left' | 'right' | 'center' }> = ({ field, children, align }) => (
    <TableCell 
      align={align}
      sx={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => handleSort(field)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
        {align === 'right' && sortField === field && (
          <Box sx={{ mr: 1 }}>
            {sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
          </Box>
        )}
        {children}
        {align !== 'right' && sortField === field && (
          <Box sx={{ ml: 1 }}>
            {sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
          </Box>
        )}
      </Box>
    </TableCell>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />} 
        aria-label="breadcrumb"
        sx={{ mb: 2 }}
      >
        <Link
          color="inherit"
          href="/"
          onClick={(e) => handleBreadcrumbClick(e, '/')}
          sx={{ 
            cursor: 'pointer',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          Home
        </Link>
        <Link
          color="inherit"
          href="/appraisals"
          onClick={(e) => handleBreadcrumbClick(e, '/appraisals')}
          sx={{ 
            cursor: 'pointer',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          Appraisals
        </Link>
        <Typography color="text.primary">New Appraisal</Typography>
      </Breadcrumbs>

      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 600,
            color: 'text.primary',
            mb: 1
          }}
        >
          Create New Appraisal
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ mb: 3 }}
        >
          Enter property details to create a new appraisal
        </Typography>
      </Box>

      {/* Tabbed Panel */}
      <Paper elevation={1}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="appraisal creation tabs"
          >
            <Tab label="Start from Bid Deal" {...a11yProps(0)} />
            <Tab label="Start from Production Deal" {...a11yProps(1)} />
            <Tab label="Start from Property" {...a11yProps(2)} />
          </Tabs>
        </Box>
        
        {/* Bid Deals Tab */}
        <TabPanel value={tabValue} index={0}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : bidDeals.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No active deals found in Appraisal Bid Pipeline
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                (Excludes deals with stages: Retainer Paid, Deal Lost)
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <SortableTableCell field="id">Deal ID</SortableTableCell>
                      <SortableTableCell field="dealname">Deal Name</SortableTableCell>
                      <SortableTableCell field="pipeline">Pipeline</SortableTableCell>
                      <SortableTableCell field="dealstage">Stage</SortableTableCell>
                      <SortableTableCell field="dealtype">Type</SortableTableCell>
                      <SortableTableCell field="amount" align="right">Amount</SortableTableCell>
                      <SortableTableCell field="primary_property">Property</SortableTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedBidDeals
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((deal) => (
                        <TableRow 
                          key={deal.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/appraisals/new/from-deal/${deal.id}`)}
                        >
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {deal.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {deal.properties.dealname || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {deal.properties.pipelineName || deal.properties.pipeline || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {deal.properties.dealstage ? (
                              <Chip 
                                label={deal.properties.dealStageName || deal.properties.dealstage}
                                size="small"
                                color={getDealStageColor(deal.properties.dealStageName || deal.properties.dealstage)}
                                variant="outlined"
                              />
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {deal.properties.dealtype || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: deal.properties.amount ? 500 : 400 }}>
                              {formatAmount(deal.properties.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {deal.properties.primary_property || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={sortedBidDeals.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </TabPanel>
        
        {/* Production Deals Tab */}
        <TabPanel value={tabValue} index={1}>
          {loadingProduction ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : errorProduction ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorProduction}
            </Alert>
          ) : productionDeals.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No active deals found in Appraisal Production Pipeline
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                (Excludes deals with stages: Deal Lost, Invoice Paid, Invoice Sent)
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <SortableTableCell field="id">Deal ID</SortableTableCell>
                      <SortableTableCell field="dealname">Deal Name</SortableTableCell>
                      <SortableTableCell field="pipeline">Pipeline</SortableTableCell>
                      <SortableTableCell field="dealstage">Stage</SortableTableCell>
                      <SortableTableCell field="dealtype">Type</SortableTableCell>
                      <SortableTableCell field="amount" align="right">Amount</SortableTableCell>
                      <SortableTableCell field="primary_property">Property</SortableTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedProductionDeals
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((deal) => (
                        <TableRow 
                          key={deal.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/appraisals/new/from-deal/${deal.id}`)}
                        >
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {deal.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {deal.properties.dealname || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {deal.properties.pipelineName || deal.properties.pipeline || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {deal.properties.dealstage ? (
                              <Chip 
                                label={deal.properties.dealStageName || deal.properties.dealstage}
                                size="small"
                                color={getDealStageColor(deal.properties.dealStageName || deal.properties.dealstage)}
                                variant="outlined"
                              />
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {deal.properties.dealtype || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: deal.properties.amount ? 500 : 400 }}>
                              {formatAmount(deal.properties.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {deal.properties.primary_property || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={sortedProductionDeals.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </TabPanel>
        
        {/* Property Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Selection Dropdowns */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Continent Selection */}
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="continent-select-label">Continent</InputLabel>
                <Select
                  labelId="continent-select-label"
                  id="continent-select"
                  value={selectedContinent || ''}
                  label="Continent"
                  onChange={(e) => {
                    const val = e.target.value as number | '';
                    if (val === '') {
                      setSelectedContinent(null);
                      deleteCookie(COOKIE_CONTINENT_ID);
                      deleteCookie(COOKIE_COUNTRY_ID);
                    } else {
                      setSelectedContinent(val as number);
                      setCookie(COOKIE_CONTINENT_ID, String(val));
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>Select a continent</em>
                  </MenuItem>
                  {continents.map(continent => (
                    <MenuItem key={continent.id} value={continent.id}>
                      {continent.name} ({continent.totalCountryCount} countries)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Country Selection */}
              <FormControl sx={{ minWidth: 200 }} disabled={!selectedContinent}>
                <InputLabel id="country-select-label">Country</InputLabel>
                <Select
                  labelId="country-select-label"
                  id="country-select"
                  value={selectedCountry || ''}
                  label="Country"
                  onChange={(e) => {
                    const val = e.target.value as number | '';
                    if (val === '') {
                      setSelectedCountry(null);
                      deleteCookie(COOKIE_COUNTRY_ID);
                    } else {
                      setSelectedCountry(val as number);
                      setCookie(COOKIE_COUNTRY_ID, String(val));
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>Select a country</em>
                  </MenuItem>
                  {countries.map(country => (
                    <MenuItem key={country.id} value={country.id}>
                      {country.name} ({country.totalMarketCount} markets)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Market Selection */}
              <FormControl sx={{ minWidth: 200 }} disabled={!selectedCountry}>
                <InputLabel id="market-select-label">Market</InputLabel>
                <Select
                  labelId="market-select-label"
                  id="market-select"
                  value={selectedMarket || ''}
                  label="Market"
                  onChange={(e) => setSelectedMarket(e.target.value as number)}
                >
                  <MenuItem value="">
                    <em>Select a market</em>
                  </MenuItem>
                  {markets.map(market => (
                    <MenuItem key={market.id} value={market.id}>
                      {market.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Property Search and List */}
            {selectedMarket && (
              <>
                <Divider />
                
                {/* Search Box */}
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search by property name or address..."
                  value={propertySearch}
                  onChange={(e) => setPropertySearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Properties List */}
                {loadingProperties ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : !Array.isArray(filteredProperties) || filteredProperties.length === 0 ? (
                  <Box sx={{ textAlign: 'center', p: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {propertySearch 
                        ? 'No properties match your search' 
                        : 'No properties found in this market'}
                    </Typography>
                  </Box>
                ) : (
                  <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                    <List>
                      {Array.isArray(filteredProperties) && filteredProperties.map((property, index) => (
                        <React.Fragment key={property.id}>
                          <ListItem 
                            sx={{ 
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'action.hover' }
                            }}
                            onClick={() => navigate(`/appraisals/new/from-property/${property.id}`)}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <LocationOnIcon fontSize="small" color="action" />
                                  <Typography variant="subtitle1" component="span">
                                    {property.property_name}
                                  </Typography>
                                  {property.chain_name && (
                                    <Chip 
                                      label={property.chain_name} 
                                      size="small" 
                                      variant="outlined"
                                    />
                                  )}
                                  {property.total_rooms && (
                                    <Typography variant="caption" color="text.secondary">
                                      {property.total_rooms} rooms
                                    </Typography>
                                  )}
                                </Box>
                              }
                              secondary={
                                <Typography variant="body2" color="text.secondary">
                                  {[
                                    property.address,
                                    property.city,
                                    property.state_province,
                                    property.postal_code,
                                    property.country
                                  ].filter(Boolean).join(', ')}
                                </Typography>
                              }
                            />
                          </ListItem>
                          {index < filteredProperties.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </Paper>
                )}

                {/* Results Summary */}
                {!loadingProperties && Array.isArray(properties) && properties.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                    Showing {Array.isArray(filteredProperties) ? filteredProperties.length : 0} of {properties.length} properties
                  </Typography>
                )}
              </>
            )}
          </Box>
        </TabPanel>
      </Paper>
    </Container>
  );
};